import { Box, Skeleton, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useRecoilValue } from 'recoil';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { IdField } from '.';

export const LabelValueBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean }>(({ theme, inline }) => ({
    display: inline ? 'flex' : 'block',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

export const LabelText = styled(Typography)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    flexShrink: 0,
}));

export const ValueText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean; component?: React.ElementType }>(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    fontSize: 12,
    fontWeight: 500,
}));

export const Textarea = styled(Typography)(() => ({
    lineHeight: 1.2,
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
}));

type LabelValueProps = {
    id: string;
    label: string;
    inline?: boolean;
};

export const LabelIdValue = ({
    id,
    label,
    inline = false,
}: LabelValueProps) => {
    return (
        <LabelValueBox inline={inline}>
            <LabelText>{`${label}`}</LabelText>
            <IdField
                id={id}
                label={undefined}
                typographyProps={{ fontSize: inline ? 12 : 14 }}
            />
        </LabelValueBox>
    );
};

type LabeledValueProps = {
    label: string | ReactNode;
    value?: string | number | ReactNode;
    loading?: boolean;
    inline?: boolean;
    sensitive?: boolean;
    testId?: string;
};

export const LabeledValue = ({
    label,
    value = 'Unknown',
    loading = false,
    inline = false,
    sensitive = false,
    testId = undefined,
}: LabeledValueProps) => {
    const randomWidth = 50 + Math.random() * 100;
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    // Suppress the native browser tooltip for sensitive values when hidden,
    // otherwise hovering would reveal the unblurred PHI string.
    const safeTitle =
        typeof value === 'string' && (!sensitive || showSensitiveInfo)
            ? value
            : undefined;
    return (
        <LabelValueBox inline={inline}>
            <LabelText title={safeTitle}>{label}</LabelText>
            {loading ? (
                <Skeleton width={randomWidth} height={14} />
            ) : (
                <ValueText
                    component="span"
                    inline={inline}
                    title={safeTitle}
                    sx={{
                        filter:
                            sensitive && !showSensitiveInfo
                                ? 'blur(0.5rem)'
                                : 'none',
                        userSelect:
                            sensitive && !showSensitiveInfo ? 'none' : 'auto',
                    }}
                    data-sensitive={sensitive && showSensitiveInfo}
                    data-testid={testId}
                >
                    {value ?? 'Unknown'}
                </ValueText>
            )}
        </LabelValueBox>
    );
};

export const LabeledTextarea = ({
    label,
    value = 'Unknown',
    loading = false,
}: LabeledValueProps) => {
    const randomWidth = 50 + Math.random() * 100;
    return (
        <LabelValueBox>
            <LabelText>{label}</LabelText>
            {loading ? (
                <Skeleton width={randomWidth} height={14} />
            ) : (
                <Textarea>{value ?? 'Unknown'}</Textarea>
            )}
        </LabelValueBox>
    );
};
